import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, Search, Loader2, Plus, X, ExternalLink, AlarmClock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getPortfolioAccounts, removeCaseFromPortfolio, addPortfolioFromLoanDisk, searchPortfolioLoans } from "@/lib/api";
import { fmtMoney, isAusDesk, ageingBasePath, getCollectionsRegion, setCollectionsRegion } from "@/lib/regionFmt";
import { useRegion } from "@/contexts/RegionContext";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const errMsg = (e, fb) => errorDetailMessage(e, fb);
const fmtWhen = (s) => (s ? new Date(s).toLocaleString(isAusDesk() ? "en-AU" : "en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");
const num = (v) => (v == null || v === "" ? null : Number(v));

function StatusChip({ r }) {
  if (r.stage === "legal" || r.legal_stage) return <Badge variant="outline" className="text-[10px] bg-red-50 text-red-800 border-red-300" data-testid={`pa-status-${r.case_id}`}>Legal</Badge>;
  if (r.in_arrears) return <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-300" data-testid={`pa-status-${r.case_id}`}><AlarmClock className="w-3 h-3 mr-1" />In arrears · {r.days_past_due ?? 0} DPD</Badge>;
  return <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300" data-testid={`pa-status-${r.case_id}`}>Current</Badge>;
}

function AddFromLoanDisk({ open, onClose, onAdded }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(null);
  const search = async () => {
    const term = q.trim();
    if (term.length < 2) return;
    setBusy(true); setRows(null);
    try {
      const res = await searchPortfolioLoans(term);
      setRows(res.loans || []);
      if (res.status === "error") toast.error(res.error || "LoanDisk search failed");
    } catch (e) { toast.error(errMsg(e, "LoanDisk search failed")); setRows([]); } finally { setBusy(false); }
  };
  const add = async (l) => {
    setAdding(l.loan_id);
    try {
      const res = await addPortfolioFromLoanDisk({
        loan_id: l.loan_id, loan_application_id: l.loan_application_id, customer_name: l.borrower_business_name || l.borrower_name,
        borrower_director_name: l.borrower_business_name ? l.borrower_name : null, borrower_id: l.borrower_id,
        principal_amount: num(l.loan_principal_amount), balance_amount: num(l.balance_amount ?? l.principal_balance_amount ?? l.loan_balance),
        released_date: l.loan_released_date, loan_product: l.loan_product_name || l.loan_product,
      });
      toast.success(res.existing ? "Existing file added to Portfolio Accounts" : "Account added to Portfolio Accounts");
      onAdded(res.case_id);
    } catch (e) { toast.error(errMsg(e, "Could not add account")); } finally { setAdding(null); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="pa-add-dialog">
        <DialogHeader><DialogTitle>Add account ({getCollectionsRegion()})</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          <Input autoFocus placeholder="SF number or company / director name" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} data-testid="pa-search-input" />
          <Button onClick={search} disabled={busy || q.trim().length < 2} data-testid="pa-search-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</Button>
        </div>
        {busy && <p className="text-xs text-muted-foreground" data-testid="pa-searching">Searching LoanDisk…</p>}
        {rows && rows.length === 0 && <p className="text-sm text-muted-foreground" data-testid="pa-search-empty">No LoanDisk loans matched.</p>}
        {rows && rows.length > 0 && (
          <div className="max-h-80 overflow-y-auto divide-y border rounded" data-testid="pa-search-results">
            {rows.map((l) => (
              <div key={l.loan_id} className="flex items-center justify-between gap-3 p-2 text-sm">
                <div className="min-w-0">
                  <p className="font-medium truncate">{l.borrower_business_name || l.borrower_name || "—"}</p>
                  <p className="text-xs text-muted-foreground font-mono">{l.loan_application_id} · {l.loan_status || "—"} · bal {fmtMoney(num(l.balance_amount ?? l.principal_balance_amount ?? l.loan_balance) || 0)}</p>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" disabled={!!adding} onClick={() => add(l)} data-testid={`pa-add-${l.loan_id}`}>{adding === l.loan_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" />Add</>}</Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PortfolioAccounts() {
  const navigate = useNavigate();
  const { currentRegion } = useRegion();
  setCollectionsRegion(currentRegion);
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [removing, setRemoving] = useState(null);
  const load = useCallback(async () => { try { setData(await getPortfolioAccounts()); } catch (e) { toast.error(errMsg(e, "Could not load Portfolio Accounts")); setData({ accounts: [], count: 0 }); } }, []);
  useEffect(() => { load(); }, [load, currentRegion]);
  const openFile = (caseId) => navigate(`${ageingBasePath()}/${caseId}?source=portfolio`);
  const remove = async (r) => {
    setRemoving(r.case_id);
    try { await removeCaseFromPortfolio(r.case_id); toast.success(`${r.customer_name || "Account"} removed from Portfolio Accounts`); load(); } catch (e) { toast.error(errMsg(e, "Remove failed")); } finally { setRemoving(null); }
  };
  const rows = data?.accounts || [];
  return (
    <div className="space-y-4" data-testid="portfolio-accounts-page">
      <div className="flex items-center justify-between bg-white border rounded-lg px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <Briefcase className="w-5 h-5 text-slate-700" />
          <div>
            <h1 className="text-lg font-semibold leading-tight" data-testid="pa-title">Portfolio Accounts</h1>
            <p className="text-xs text-muted-foreground">Live loans the team is working outside arrears — shared list, same Customer File. {data ? `${data.count} account${data.count === 1 ? "" : "s"}` : ""}</p>
          </div>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)} data-testid="pa-add-btn"><Plus className="w-4 h-4 mr-1" />Add account</Button>
      </div>

      {!data ? <div className="p-10 text-center text-sm text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        : rows.length === 0 ? (
          <div className="border rounded-lg bg-white p-12 text-center space-y-2" data-testid="pa-empty">
            <Briefcase className="w-8 h-8 mx-auto text-slate-300" />
            <p className="text-sm font-medium">No portfolio accounts yet</p>
            <p className="text-xs text-muted-foreground max-w-md mx-auto">Open any account in Loan Arrears Ageing and press <b>Add to Portfolio Accounts</b>, or use <b>Add account</b> to pull in a current loan. It stays here — visible to everyone with collections access — until someone removes it.</p>
          </div>
        ) : (
          <div className="border rounded-lg bg-white overflow-hidden">
            <table className="w-full text-sm" data-testid="pa-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr><th className="text-left px-3 py-2">Customer</th><th className="text-left px-3 py-2">Status</th><th className="text-right px-3 py-2">Balance</th><th className="text-left px-3 py-2">Handler</th><th className="text-left px-3 py-2">Last activity</th><th className="text-left px-3 py-2">Added</th><th className="px-3 py-2" /></tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.case_id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openFile(r.case_id)} data-testid={`pa-row-${r.case_id}`}>
                    <td className="px-3 py-2"><p className="font-medium">{r.customer_name || "—"}</p><p className="text-xs text-muted-foreground font-mono">{r.loan_application_id}{r.borrower_director_name ? ` · ${r.borrower_director_name}` : ""}</p></td>
                    <td className="px-3 py-2"><StatusChip r={r} />{r.open_p2ps > 0 && <span className="ml-1 text-[10px] text-muted-foreground">{r.open_p2ps} open P2P</span>}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.balance_amount != null ? fmtMoney(r.balance_amount) : "—"}</td>
                    <td className="px-3 py-2 text-xs">{r.handler || <span className="text-muted-foreground">Unassigned</span>}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{fmtWhen(r.last_activity_at)}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.added_by || "—"}<br />{fmtWhen(r.added_at)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openFile(r.case_id)} data-testid={`pa-open-${r.case_id}`}><ExternalLink className="w-3.5 h-3.5 mr-1" />Open</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-red-700" disabled={removing === r.case_id} onClick={() => remove(r)} data-testid={`pa-remove-${r.case_id}`}>{removing === r.case_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><X className="w-3.5 h-3.5 mr-1" />Remove</>}</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      <AddFromLoanDisk open={addOpen} onClose={() => setAddOpen(false)} onAdded={(caseId) => { setAddOpen(false); load(); openFile(caseId); }} />
    </div>
  );
}
